import { useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { EMAIL_PATTERN, GENERIC_ERROR_MESSAGE, NETWORK_ERROR_MESSAGE } from '../../../config/forms'
import { PublicFormRouteEnum, publicFormUrl } from '../../../config/publicApi'
import { sectionAnchors } from '../../../config/routes'

// Sent as `contactReason` when the honeypot checkbox is ticked; the API's
// scorer only tests the field non-blank, so any non-empty string works —
// this one just reads sensibly in a captured payload or log line.
const DECOY_TICKED_VALUE = 'yes'

type LeadFormValues = {
  name: string
  email: string
  school: string
  message: string
}

const EMPTY_FORM: LeadFormValues = { name: '', email: '', school: '', message: '' }

// Waitlist/contact form — validates name and email inline before posting.
// It carries name/email + optional school/message, so it posts to the
// waitlist endpoint (the combined signup target).
export const ContactSection = () => {
  const [values, setValues] = useState<LeadFormValues>(EMPTY_FORM)
  const [invalidFields, setInvalidFields] = useState<Set<'name' | 'email'>>(new Set())
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const decoyRef = useRef<HTMLInputElement>(null)
  const dwellStartRef = useRef<number | null>(null)

  const updateField = (field: keyof LeadFormValues) => (e: FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // Read the value BEFORE the async state updater runs — React nulls the
    // pooled event's currentTarget by the time the updater callback executes.
    const nextValue = e.currentTarget.value
    if (dwellStartRef.current === null) dwellStartRef.current = Date.now()
    setValues((prev) => ({ ...prev, [field]: nextValue }))
    setInvalidFields((prev) => {
      if (!prev.has(field as 'name' | 'email')) return prev
      const next = new Set(prev)
      next.delete(field as 'name' | 'email')
      return next
    })
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setServerError(null)

    const nextInvalid = new Set<'name' | 'email'>()
    if (!values.name.trim()) nextInvalid.add('name')
    if (!values.email.trim() || !EMAIL_PATTERN.test(values.email.trim())) nextInvalid.add('email')
    setInvalidFields(nextInvalid)

    if (nextInvalid.size > 0) return

    const url = publicFormUrl(PublicFormRouteEnum.Waitlist)
    if (!url) {
      setServerError(GENERIC_ERROR_MESSAGE)
      return
    }

    try {
      setSubmitting(true)
      const contactReason = decoyRef.current?.checked ? DECOY_TICKED_VALUE : ''
      const dwellMs = dwellStartRef.current === null ? null : Date.now() - dwellStartRef.current
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...values, contactReason, dwellMs }),
      })
      const result = await response.json().catch(() => ({}))

      if (!response.ok || !result?.success) {
        setServerError(result?.error ?? GENERIC_ERROR_MESSAGE)
        return
      }

      setSubmitted(true)
      setValues(EMPTY_FORM)
      if (decoyRef.current) decoyRef.current.checked = false
      dwellStartRef.current = null
    } catch {
      setServerError(NETWORK_ERROR_MESSAGE)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section id={sectionAnchors.contact.slice(1)} className="band">
      <div className="wrap contact-grid">
        <div className="contact-copy">
          <span className="eyebrow">Get started</span>
          <h2>Spend less time marking, more time teaching.</h2>
          <p>
            Get started today, or tell us about your school and we&apos;ll be in touch. We&apos;ll only use your details
            to reach out about QuickScore.
          </p>
        </div>
        <form className="lead" onSubmit={handleSubmit} noValidate>
          {submitted ? (
            <div className="form-ok show">Thanks! We&apos;ve got your message — we&apos;ll be in touch soon. ✓</div>
          ) : null}

          {serverError ? (
            <div className="form-err show" role="alert">
              {serverError}
            </div>
          ) : null}

          <div className={`field${invalidFields.has('name') ? ' invalid' : ''}`}>
            <label htmlFor="f-name">Name</label>
            <input
              id="f-name"
              name="name"
              type="text"
              placeholder="Your name"
              required
              value={values.name}
              onChange={updateField('name')}
              disabled={submitting}
            />
            <div className="err">Please enter your name.</div>
          </div>

          <div className={`field${invalidFields.has('email') ? ' invalid' : ''}`}>
            <label htmlFor="f-email">Email</label>
            <input
              id="f-email"
              name="email"
              type="email"
              placeholder="you@school.edu"
              required
              value={values.email}
              onChange={updateField('email')}
              disabled={submitting}
            />
            <div className="err">Please enter a valid email address.</div>
          </div>

          <div className="field">
            <label htmlFor="f-school">
              School <span className="field-optional">(optional)</span>
            </label>
            <input
              id="f-school"
              name="school"
              type="text"
              placeholder="Your school"
              value={values.school}
              onChange={updateField('school')}
              disabled={submitting}
            />
          </div>

          <div className="field">
            <label htmlFor="f-msg">
              Message <span className="field-optional">(optional)</span>
            </label>
            <textarea
              id="f-msg"
              name="message"
              rows={3}
              placeholder="Tell us what you teach"
              value={values.message}
              onChange={updateField('message')}
              disabled={submitting}
            />
          </div>

          <span className="hp-field" aria-hidden="true">
            <input
              name="contactReason"
              type="checkbox"
              ref={decoyRef}
              defaultChecked={false}
              tabIndex={-1}
              autoComplete="off"
              data-1p-ignore
              data-lpignore="true"
              data-bwignore="true"
              data-form-type="other"
              data-protonpass-ignore
            />
          </span>

          <button className="btn btn-primary" type="submit" disabled={submitting}>
            {submitting ? 'Sending…' : 'Contact Us'}
          </button>
        </form>
      </div>
    </section>
  )
}
